import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import { renderSceneToDataUrl } from '../rendering/scene-render-helpers';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolExecutionContext,
} from '../types';

import { ConversationTool } from './ConversationTool';

import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import { setSceneReviewing } from 'js/src/data/agent-activity';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';

export const legacyImageAskSchema = z.object({
    media_ids: z
        .array(z.string())
        .optional()
        .describe('IDs of media from the media registry to include as images.'),
    scene_indexes: z
        .array(z.int())
        .optional()
        .describe('Indexes of scenes (0, 1, 2, ...) to render and include as images.'),
    question: z
        .string()
        .describe(
            'A detailed question to ask about the provided image(s). Be specific about what you want to know. When referring to scenes, use their zero-based index (e.g. "scene index 3") to match the image labels - do not use 1-based scene numbers.',
        ),
});
export type ImageAskArgs = z.infer<typeof legacyImageAskSchema>;

type ResolvedImage = { url: string } | { error: string };

/**
 * Resolves image references (media registry ids and/or scene indexes) into image
 * content blocks and asks a vision model a question about them. Can mix media ids
 * and scene indexes so the model can reason about how a piece of media appears
 * within a scene (e.g. to decide cropping or placement).
 *
 * Returns a text answer only - does not inject images back into the parent
 * conversation.
 */
export class LegacyImageAskTool extends ConversationTool<typeof legacyImageAskSchema> {
    public static readonly toolName = 'image_ask';

    protected override readonly schema = legacyImageAskSchema;
    protected override readonly toolName = LegacyImageAskTool.toolName;
    protected override readonly toolDescription =
        'Ask a vision model a detailed question about one or more images. Images are referenced by media registry id (`media_ids`) and/or by scene index (`scene_indexes`), and you can mix both - for example to understand how a specific piece of media appears inside a scene when deciding cropping or placement. Returns a text answer. In the `question`, always refer to scenes by their zero-based index (e.g. "scene index 3"), matching how the images are labeled - never use a 1-based scene number, as that will not line up with the images the vision model sees.';

    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_4_MINI;
    public override readonly childReasoningEffort = ReasoningEffort.NONE;

    public override getToolDisplayName(): string {
        return 'Reviewing scene';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ImageAskArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const mediaIds = args.media_ids ?? [];
        const sceneIndexes = args.scene_indexes ?? [];

        if (mediaIds.length === 0 && sceneIndexes.length === 0) {
            return {
                result: 'Error: provide at least one media id (media_ids) or scene index (scene_indexes) to ask about.',
                isSuccess: false,
            };
        }

        const reviewingSceneIds = sceneIndexes
            .map((index) => this.luminary.scenes[index]?.id)
            .filter((id): id is string => id != null);
        if (reviewingSceneIds.length > 0) {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
            // A reviewed scene is considered filled — clear its build-preview blur
            // right away (on its own, no need to wait for both text and media)
            // rather than after the vision call returns.
            this.recordSceneFillForScenes(sceneIndexes, 'review');
        }
        try {
            return await this.askAboutImages(args, context, mediaIds, sceneIndexes);
        } finally {
            if (reviewingSceneIds.length > 0) {
                this.dispatch(
                    setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }),
                );
            }
        }
    }

    private async askAboutImages(
        args: ImageAskArgs,
        context: ToolExecutionContext,
        mediaIds: string[],
        sceneIndexes: number[],
    ): Promise<EnhancedToolResult> {
        const { question } = args;
        const messageContent: (TextContent | ImageContent)[] = [];

        for (const id of mediaIds) {
            const resolved = this.resolveMediaImageUrl(id);
            if ('error' in resolved) {
                return { result: resolved.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Media id ${id}:` },
                { type: 'image_url', image_url: { url: resolved.url } },
            );
        }

        for (const sceneIndex of sceneIndexes) {
            const rendered = await this.renderSceneToImageUrl(sceneIndex);
            if ('error' in rendered) {
                return { result: rendered.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Scene index ${sceneIndex}:` },
                { type: 'image_url', image_url: { url: rendered.url } },
            );
        }

        messageContent.push({
            type: 'text',
            text: `Answer the following question about the image(s) above as specifically and concisely as possible. Do not explain your reasoning unless asked.\n\nQuestion: ${question}`,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are analyzing one or more images from a video. Answer the user's question about what you see.

Be specific and concise. When describing locations within an image, use terms like: top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [],
            task: 'image_ask_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });
            if (!result.success) {
                // A failed child conversation does not always carry a message; interpolating it
                // blind produced a literal "undefined" the agent could not act on.
                return {
                    result: `Error during image analysis: ${result.message || 'the vision model did not return a result. Try again in a moment.'}`,
                    isSuccess: false,
                };
            }
            return {
                isSuccess: true,
                result: result.message || 'No answer returned from image analysis.',
            };
        } catch (error) {
            return {
                result: `Error asking about images: ${error instanceof Error ? error.message : 'Unknown error'}`,
                isSuccess: false,
            };
        }
    }

    private resolveMediaImageUrl(id: string): ResolvedImage {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaData = mediaRegistry.getItem(id);
        if (!mediaData) {
            return { error: `Error: Media with ID "${id}" not found in registry.` };
        }

        const representation = mediaRegistry.getRepresentation(id);
        if (!representation) {
            return { error: `Error: Could not get representation for media ID "${id}".` };
        }

        // The "invisible" asset is a transparent placeholder used to show the
        // scene background - there's nothing meaningful to look at.
        if (
            mediaData.type === AssetContainerTypes.BACKGROUND &&
            mediaData.background?.type === SlideBackgroundModelTypeEnum.IMAGE &&
            mediaData.background?.image?.source === INVISIBLE_IMAGE_SOURCE_DATA.source &&
            mediaData.background?.image?.source_id === INVISIBLE_IMAGE_SOURCE_DATA.source_id
        ) {
            return {
                error: `Error: Media with ID "${id}" is an invisible/transparent image (effectively unset).`,
            };
        }

        if (representation.type === 'image' && mediaData.background?.image?.preview_url) {
            return { url: mediaData.background.image.preview_url };
        }
        if (representation.type === 'video' && mediaData.background?.video?.poster_url) {
            return { url: mediaData.background.video.poster_url };
        }
        const expectedField = representation.type === 'video' ? 'poster_url' : 'preview_url';
        return { error: `Error: Could not extract ${expectedField} from media ID "${id}".` };
    }

    private async renderSceneToImageUrl(sceneIndex: number): Promise<ResolvedImage> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        const imageData = await renderSceneToDataUrl(this.luminary, scene, { subsceneIndex: 0 });
        if (imageData == null) {
            return {
                error: `Error: timed out rendering scene ${sceneIndex}. The video preview may be busy - try again in a moment.`,
            };
        }
        return { url: imageData };
    }
}
